import React, { useMemo, useState } from "react";
import { MdCheck, MdContentCopy, MdDeleteSweep, MdFactCheck, MdSmartToy, MdSync, MdAccountTree } from "react-icons/md";
import { IssueList, OkLine, copyText } from "./EditorParts";
import { buildTranslationPackage, compareStructures, compareTranslation, getIn, isPlainObject, languageName, listLeaves, parseJsonWithLocation, removeExtraKeys, syncMissingKeys } from "./contentUtils";

// Keeps the languages of ONE email on the same structure: same keys, only
// the texts differ. Sync adds missing KEYS (empty texts, or the source text
// as an explicit fallback per settings), never pretends a text is
// translated, and never deletes anything without a confirmation. Copy /
// import packages make external translation (ChatGPT, Claude, Gemini,
// Google Translate...) safe: the pasted result is analyzed before it can
// replace the target language draft, which is then saved explicitly.
export default function TranslationsPanel({ languages, sourceLanguage, drafts, setDocument, t, settings, notify, languageLabel }) {
  const [source, setSource] = useState(sourceLanguage);
  const [target, setTarget] = useState(() => languages.find((code) => code !== sourceLanguage) || sourceLanguage);
  const [importText, setImportText] = useState("");
  const [importReport, setImportReport] = useState(null);
  const sourceDoc = useMemo(() => drafts[source]?.value || {}, [drafts, source]);
  const targetDoc = useMemo(() => drafts[target]?.value || {}, [drafts, target]);
  const report = useMemo(() => compareStructures(sourceDoc, targetDoc), [sourceDoc, targetDoc]);
  const fillMode = settings?.sync?.fillMode || "empty";
  const label = (code) => languageLabel?.(code) || languageName(code);

  const sync = () => {
    const { document, added } = syncMissingKeys(sourceDoc, targetDoc, { fill: fillMode });
    setDocument(target, document);
    notify?.(t.syncDone.replace("{count}", added.length).replace("{language}", label(target)));
  };
  const copyStructure = () => {
    const synced = syncMissingKeys(sourceDoc, targetDoc, { fill: fillMode }).document;
    const pruned = removeExtraKeys(sourceDoc, synced);
    if (pruned.removed.length && !window.confirm(t.confirmRemoveExtra.replace("{keys}", pruned.removed.join(", ")))) return;
    setDocument(target, pruned.document);
    notify?.(t.structureCopied.replace("{language}", label(target)));
  };
  const removeExtra = () => {
    const pruned = removeExtraKeys(sourceDoc, targetDoc);
    if (!window.confirm(t.confirmRemoveExtra.replace("{keys}", pruned.removed.join(", ")))) return;
    setDocument(target, pruned.document);
  };
  const analyze = () => {
    const parsed = parseJsonWithLocation(importText.trim().replace(/^```(?:json)?\s*\n?|```\s*$/g, ""));
    if (!parsed.ok) return setImportReport({ error: parsed.error.message });
    if (!isPlainObject(parsed.value)) return setImportReport({ error: t.rootMustBeObject });
    return setImportReport({ value: parsed.value, ...compareTranslation(sourceDoc, parsed.value) });
  };
  const apply = () => {
    setDocument(target, importReport.value);
    setImportText(""); setImportReport(null);
    notify?.(t.importApplied.replace("{language}", label(target)));
  };

  const languageSelect = (value, onChange, aria) => <select className="eck-input eck-input--inline" aria-label={aria} value={value} onChange={(e) => { onChange(e.target.value); setImportReport(null); }}>
    {languages.map((code) => <option key={code} value={code}>{label(code)}</option>)}
  </select>;

  // Every language against the source at once (missing / extra keys).
  // Counted in VALUES (leaves): a missing group of 30 texts is 30 missing.
  const sourceLeaves = useMemo(() => listLeaves(sourceDoc), [sourceDoc]);
  const overview = useMemo(() => languages.filter((code) => code !== source).map((code) => {
    const target = drafts[code]?.value || {};
    const diff = compareStructures(sourceDoc, target);
    return { code, ...diff, missingValues: sourceLeaves.filter((leaf) => getIn(target, leaf.parts) === undefined).length, extraValues: listLeaves(target).filter((leaf) => getIn(sourceDoc, leaf.parts) === undefined).length };
  }), [languages, source, sourceDoc, sourceLeaves, drafts]);

  return <div className="eck-translations">
    <section className="eck-panel">
      <h5>{t.translationOverview.replace("{source}", label(source))}</h5>
      <ul className="eck-lang-report">
        {overview.map((entry) => <li key={entry.code}>
          <strong>{label(entry.code)}</strong>
          {entry.inSync ? <span className="eck-pill is-ok">{t.complete}</span> : <>
            {entry.missingValues > 0 && <span className="eck-pill is-warn">{t.missingCount.replace("{count}", entry.missingValues)}</span>}
            {entry.extraValues > 0 && <span className="eck-pill">{t.extraCount.replace("{count}", entry.extraValues)}</span>}
            {entry.typeMismatches.length > 0 && <span className="eck-pill is-bad">{t.typeCount.replace("{count}", entry.typeMismatches.length)}</span>}
          </>}
          {entry.code !== target && <button type="button" className="eck-link-btn eck-link-btn--small" onClick={() => setTarget(entry.code)}>{t.review}</button>}
        </li>)}
      </ul>
    </section>
    <div className="eck-toolbar">
      <label className="eck-inline-label">{t.sourceLanguage} {languageSelect(source, setSource, t.sourceLanguage)}</label>
      <span aria-hidden="true">→</span>
      <label className="eck-inline-label">{t.targetLanguage} {languageSelect(target, setTarget, t.targetLanguage)}</label>
    </div>

    <section className="eck-panel">
      <h5>{t.structureReport.replace("{source}", label(source)).replace("{target}", label(target))}</h5>
      {source === target ? <p className="eck-hint">{t.chooseDifferentLanguages}</p> : <>
        {report.inSync && <OkLine>{t.structureInSync}</OkLine>}
        {report.missing.length > 0 && <div className="eck-keylist is-error"><strong>{t.missingKeys.replace("{count}", report.missing.length)}</strong> {report.missing.map((path) => <code key={path}>{path}</code>)}</div>}
        {report.extra.length > 0 && <div className="eck-keylist is-warning"><strong>{t.extraKeys.replace("{count}", report.extra.length)}</strong> {report.extra.map((path) => <code key={path}>{path}</code>)}</div>}
        {report.typeMismatches.length > 0 && <div className="eck-keylist is-warning"><strong>{t.typeMismatches}</strong> {report.typeMismatches.map((m) => <code key={m.path}>{`${m.path} (${m.expected} → ${m.actual})`}</code>)}</div>}
        <div className="eck-toolbar">
          <button type="button" className="eck-btn" disabled={!report.missing.length} onClick={sync}><MdSync aria-hidden="true" /> {t.syncMissing}</button>
          <button type="button" className="eck-btn" onClick={copyStructure}><MdAccountTree aria-hidden="true" /> {t.copyStructure}</button>
          <button type="button" className="eck-btn" disabled={!report.extra.length} onClick={removeExtra}><MdDeleteSweep aria-hidden="true" /> {t.removeExtra}</button>
        </div>
        <p className="eck-hint">{fillMode === "source" ? t.syncFillSource : t.syncFillEmpty} {t.syncNotSaved}</p>
      </>}
    </section>

    <section className="eck-panel">
      <h5>{t.externalTranslation}</h5>
      <p className="eck-hint">{t.externalTranslationHint}</p>
      <div className="eck-toolbar">
        <button type="button" className="eck-btn" onClick={() => copyText(buildTranslationPackage({ document: sourceDoc, sourceLanguage: source, targetLanguage: target, withPrompt: false })).then(() => notify?.(t.copied))}><MdContentCopy aria-hidden="true" /> {t.copyJsonForTranslation}</button>
        <button type="button" className="eck-btn eck-btn--primary" onClick={() => copyText(buildTranslationPackage({ document: sourceDoc, sourceLanguage: source, targetLanguage: target, customInstructions: settings?.ai?.customInstructions })).then(() => notify?.(t.promptCopied))}><MdSmartToy aria-hidden="true" /> {t.copyPromptAndJson}</button>
      </div>
      <label className="eck-block-label" htmlFor="eck-import">{t.pasteTranslated.replace("{language}", label(target))}</label>
      <textarea id="eck-import" className="eck-code__area eck-code__area--short" spellCheck={false} value={importText} onChange={(e) => { setImportText(e.target.value); setImportReport(null); }} placeholder='{ "title": "..." }' />
      <div className="eck-toolbar">
        <button type="button" className="eck-btn" disabled={!importText.trim()} onClick={analyze}><MdFactCheck aria-hidden="true" /> {t.analyze}</button>
        <span className="eck-toolbar__spacer" />
        {importReport?.value && <button type="button" className="eck-btn eck-btn--primary" onClick={apply}><MdCheck aria-hidden="true" /> {t.applyToLanguage.replace("{language}", label(target))}</button>}
      </div>
      {importReport?.error && <div className="eck-alert" role="alert">{importReport.error}</div>}
      {importReport?.value && <div className="eck-report">
        {importReport.inSync && !importReport.warnings.length && <OkLine>{t.importClean}</OkLine>}
        {importReport.missing.length > 0 && <div className="eck-keylist is-error"><strong>{t.missingKeys.replace("{count}", importReport.missing.length)}</strong> {importReport.missing.map((path) => <code key={path}>{path}</code>)}</div>}
        {importReport.extra.length > 0 && <div className="eck-keylist is-warning"><strong>{t.extraKeys.replace("{count}", importReport.extra.length)}</strong> {importReport.extra.map((path) => <code key={path}>{path}</code>)}</div>}
        <IssueList warnings={[...importReport.typeMismatches.map((m) => `${m.path}: ${m.expected} → ${m.actual}`), ...importReport.warnings]} t={t} />
        <p className="eck-hint">{t.importOnlyTarget.replace("{language}", label(target))}</p>
      </div>}
    </section>
  </div>;
}
